import { observer } from 'mobx-react-lite'
import * as React from 'react'
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'

import { useStores } from 'hooks/useStores'

import { AppHeader } from 'components/AppHeader'
import {
  AcquiredClientsPage,
  AcquiredClientsReports,
  ApplicationAutoReports,
  ApplicationReports,
  ApplicationsLayout,
  ApplicationsPage,
  InstantCardPage,
  MBonusPage,
  MPlusPage,
  ManualReportsLayout,
  MbusinessPage,
  MkassaPage,
  MpostPage,
  MqrPage,
  NonResidentPage,
  OpenMBPage,
  RevivalPage,
  RevivalReports,
} from 'pages/Applications'
import { AttractorsPage } from 'pages/Attractors/AttractorsPage'
import { AuthPage } from 'pages/Auth/AuthPage'
import { CalculatorsPage } from 'pages/Calculators/CalculatorsPage'
import { CalculatorPlanCreate } from 'pages/Calculators/CreatePage'
import { CalculatorPlanEdit } from 'pages/Calculators/EditPage'
import { ContactCenterPage } from 'pages/ContactCenter'
import { ContactCenterFillialsPage } from 'pages/ContactCenterFillials'
import {
  FillialsActsPage,
  FillialsAppsPage,
  LogisitcPageLayout,
  LogisticReports,
  NewCartsPage,
  ProcessedPage,
} from 'pages/Logistic'
import { SupervisorsPage } from 'pages/Supervisors/SupervisorsPage'
import { UsersPage } from 'pages/Users/UsersPage'

import { UserRole } from 'models/user'

// import { AuthStorage } from '../services/storage/AuthStorage'
import { ProtectedRoute } from './PrivateRoute'
import { ROUTER_NAMES } from './routerNames'
import {
  ROUTER_REDIRECTS,
  ROUTER_RELOAD_DOCUMENT_REDIRECTS,
} from './routerRoles'

const Layout = () => {
  const [mounted, setMounted] = React.useState(false)
  const { userStore } = useStores()

  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        await userStore.getUser()
      } catch (error) {
      } finally {
        setMounted(true)
      }
    }
    checkAuth()
  }, [userStore])

  if (!mounted) {
    return null
  }
  return (
    <>
      <AppHeader />
      <Outlet />
    </>
  )
}

const RoleRedirect = observer(() => {
  const { userStore } = useStores()
  const redirectTo = userStore.user?.role
    ? ROUTER_REDIRECTS[userStore.user.role as UserRole]
    : ROUTER_NAMES.AUTH

  React.useEffect(() => {
    if (ROUTER_RELOAD_DOCUMENT_REDIRECTS.includes(redirectTo)) {
      window.location.replace(redirectTo)
    }
  }, [redirectTo])

  if (ROUTER_RELOAD_DOCUMENT_REDIRECTS.includes(redirectTo)) {
    return null
  }

  return <Navigate to={redirectTo} replace />
})

export const AppRouter = observer(() => {
  return (
    <BrowserRouter>
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route
              path={ROUTER_NAMES.APPLICATIONS}
              element={
                <ProtectedRoute
                  roles={[
                    UserRole.Administrator,
                    UserRole.ONLY_VIEW,
                    UserRole.Controller,
                    UserRole.SuperController,
                    UserRole.Manager,
                  ]}
                >
                  <ApplicationsLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<ApplicationsPage />} />
              <Route path={ROUTER_NAMES.MKASSA} element={<MkassaPage />} />
              <Route path={ROUTER_NAMES.MPOST} element={<MpostPage />} />
              <Route
                path={ROUTER_NAMES.MBUSINESS}
                element={<MbusinessPage />}
              />
              <Route
                path={ROUTER_NAMES.OPEN_MBUSINESS}
                element={<OpenMBPage />}
              />
              <Route path={ROUTER_NAMES.MQR} element={<MqrPage />} />
              <Route
                path={ROUTER_NAMES.NON_RESIDENT}
                element={<NonResidentPage />}
              />
              <Route path={ROUTER_NAMES.MPLUS} element={<MPlusPage />} />
              <Route
                path={ROUTER_NAMES.INSTANT_CARD}
                element={<InstantCardPage />}
              />
              <Route path={ROUTER_NAMES.MBONUS} element={<MBonusPage />} />
              <Route path={ROUTER_NAMES.REVIVAL} element={<RevivalPage />} />
              <Route
                path={ROUTER_NAMES.ACQUIRED_CLIENTS}
                element={<AcquiredClientsPage />}
              />
              <Route
                path={ROUTER_NAMES.MANUAL_REPORTS}
                element={<ManualReportsLayout />}
              >
                <Route index element={<ApplicationReports />} />
                <Route
                  path={ROUTER_NAMES.REVIVAL_REPORTS}
                  element={<RevivalReports />}
                />
                <Route
                  path={ROUTER_NAMES.ACQUIRED_CLIENTS_REPORTS}
                  element={<AcquiredClientsReports />}
                />
              </Route>
              <Route
                path={ROUTER_NAMES.APPLICATION_AUTO_REPORTS}
                element={<ApplicationAutoReports />}
              />
            </Route>
            <Route
              path={ROUTER_NAMES.ATTRACTORS}
              element={
                <ProtectedRoute roles={[UserRole.SuperController]}>
                  <AttractorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTER_NAMES.CALCULATORS}
              element={
                <ProtectedRoute roles={[UserRole.Administrator]}>
                  <CalculatorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${ROUTER_NAMES.CALCULATORS}/create`}
              element={
                <ProtectedRoute roles={[UserRole.Administrator]}>
                  <CalculatorPlanCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path={`${ROUTER_NAMES.CALCULATORS}/:plan_id`}
              element={
                <ProtectedRoute roles={[UserRole.Administrator]}>
                  <CalculatorPlanEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTER_NAMES.SUPERVISORS}
              element={
                <ProtectedRoute
                  roles={[UserRole.Administrator, UserRole.ONLY_VIEW]}
                >
                  <SupervisorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTER_NAMES.USERS}
              element={
                <ProtectedRoute
                  roles={[
                    UserRole.Administrator,
                    UserRole.ONLY_VIEW,
                    UserRole.AdministratorCIB,
                  ]}
                >
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTER_NAMES.LOGISTIC}
              element={
                <ProtectedRoute
                  roles={[
                    UserRole.Administrator,
                    UserRole.ONLY_VIEW,
                    UserRole.ControllerUKC,
                    UserRole.Author,
                    UserRole.Executor,
                  ]}
                >
                  <LogisitcPageLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<NewCartsPage />} />
              <Route
                path={ROUTER_NAMES.LOGISTIC_PROCESSED}
                element={<ProcessedPage />}
              />
              <Route
                path={ROUTER_NAMES.LOGISTIC_FILLIALS_APPS}
                element={<FillialsAppsPage />}
              />
              <Route
                path={ROUTER_NAMES.LOGISTIC_FILLIALS_ACTS}
                element={<FillialsActsPage />}
              />
              <Route
                path={ROUTER_NAMES.LOGISTIC_REPORTS}
                element={<LogisticReports />}
              />
            </Route>
            <Route
              path={ROUTER_NAMES.CONTACT_CENTER}
              element={
                <ProtectedRoute
                  roles={[
                    UserRole.Administrator,
                    UserRole.ONLY_VIEW,
                    UserRole.Specialist,
                    UserRole.ControllerUKC,
                  ]}
                >
                  <ContactCenterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTER_NAMES.FILIAL}
              element={
                <ProtectedRoute
                  roles={[
                    UserRole.Administrator,
                    UserRole.ONLY_VIEW,
                    UserRole.Specialist,
                    UserRole.ControllerUKC,
                  ]}
                >
                  <ContactCenterFillialsPage />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<RoleRedirect />} />
            <Route path="*" element={<></>} />
          </Route>
          <Route path={ROUTER_NAMES.AUTH} element={<AuthPage />} />
        </Routes>
      </QueryParamProvider>
    </BrowserRouter>
  )
})
